import { ApolloPermission } from "@vega-api/generated/types/apolloPermission";

export enum UiCapabilityKey {
  CanViewFundOpportunities = "canViewFundOpportunities",
  CanViewFundOpportunityUserAccess = "canViewFundOpportunityUserAccess",
  CanEditFundOpportunityDetails = "canEditFundOpportunityDetails",
  CanManageFundOpportunityUserAccess = "canManageFundOpportunityUserAccess",
  CanTriggerFundInvestorDocumentsNotification = "canTriggerFundInvestorDocumentsNotification",
  CanTriggerFundOfferingsDocumentsNotification = "canTriggerFundOfferingsDocumentsNotification",
  CanTriggerFundInvestorCapitalActivitiesNotification = "canTriggerFundInvestorCapitalActivitiesNotification",
  CanViewFundInvestments = "canViewFundInvestments",
  CanViewFundInvestorDocuments = "canViewFundInvestorDocuments",
  CanUploadFundInvestmentDocuments = "canUploadFundInvestmentDocuments",
  CanViewFundPage = "canViewFundPage",
  CanViewFundSubscriptions = "canViewFundSubscriptions",
  CanViewFundCapitalActivities = "canViewFundCapitalActivities",
  CanViewFundInvestmentUserAccess = "canViewFundInvestmentUserAccess",
  CanManageFundInvestmentUserAccess = "canManageFundInvestmentUserAccess",
  CanManageFundInvestmentMetrics = "canManageFundInvestmentMetrics",
  CanManageOpportunityContacts = "canManageOpportunityContacts",
  CanInviteUsers = "canInviteUsers",
  CanManageFundCasConfigs = "canManageFundCasConfigs",
  CanAccessFund = "canAccessFund",
  CanAccessGeneralFundMaterials = "canAccessGeneralFundMaterials",
  CanAccessDocumentAnalytics = "canAccessDocumentAnalytics",
  CanViewOrgDetails = "canViewOrgDetails",
  CanViewUserDetails = "canViewUserDetails",
  CanEditUserDetails = "canEditUserDetails",
  CanManageUserDetails = "canManageUserDetails",
  CanViewUserCarry = "canViewUserCarry",
  CanImpersonate = "canImpersonate",
  CanViewWorkbenchCollaborators = "canViewWorkbenchCollaborators",
  CanManageWorkbenchCollaborators = "canManageWorkbenchCollaborators",
  CanViewGeneralPlatformAnalytics = "canViewGeneralPlatformAnalytics",
  CanActAsVegaAdmin = "canActAsVegaAdmin",
  CanEditEducation = "canEditEducation",
  CanEditEducationPages = "canEditEducationPages",
  CanTriggerOcrFunctionality = "canTriggerOcrFunctionality",
  CanActionSubscriptionTracker = "canActionSubscriptionTracker",
  CanViewEmployeeSubscriptionTracker = "canViewEmployeeSubscriptionTracker",
  CanViewThirdPartySubscriptionTracker = "canViewThirdPartySubscriptionTracker",
  CanDownloadSubscriptionOpinionIssuanceReports = "canDownloadSubscriptionOpinionIssuanceReports",
  CanSubmitCase = "canSubmitCase",
  CanAssignCase = "canAssignCase",
  CanCloseCase = "canCloseCase",
  CanViewAllCases = "canViewAllCases",
  CanUpdateCase = "canUpdateCase",
  CanDeleteCase = "canDeleteCase",
  CanCommentOnCase = "canCommentOnCase",
  CanSendCaseEmail = "canSendCaseEmail",
  CanManageCaseAttachments = "canManageCaseAttachments",
  CanConfigureCases = "canConfigureCases",
  CanCreateCaseTemplate = "canCreateCaseTemplate",
  CanUpdateCaseTemplate = "canUpdateCaseTemplate",
}

export const uiCapabilityKeys = Object.values(UiCapabilityKey);

export type UiCapabilities = Record<UiCapabilityKey, boolean>;

export const legacyPermissionByCapability: Partial<
  Record<UiCapabilityKey, ApolloPermission>
> = {
  [UiCapabilityKey.CanViewFundOpportunities]:
    ApolloPermission.VIEW_FUND_OPPORTUNITIES,
  [UiCapabilityKey.CanViewFundOpportunityUserAccess]:
    ApolloPermission.VIEW_FUND_OPPORTUNITY_USER_ACCESS,
  [UiCapabilityKey.CanEditFundOpportunityDetails]:
    ApolloPermission.EDIT_FUND_OPPORTUNITY_DETAILS,
  [UiCapabilityKey.CanManageFundOpportunityUserAccess]:
    ApolloPermission.MANAGE_FUND_OPPORTUNITY_USER_ACCESS,
  [UiCapabilityKey.CanTriggerFundInvestorDocumentsNotification]:
    ApolloPermission.TRIGGER_FUND_INVESTOR_DOCUMENTS_NOTIFICATION,
  [UiCapabilityKey.CanTriggerFundOfferingsDocumentsNotification]:
    ApolloPermission.TRIGGER_FUND_OFFERINGS_DOCUMENTS_NOTIFICATION,
  [UiCapabilityKey.CanTriggerFundInvestorCapitalActivitiesNotification]:
    ApolloPermission.TRIGGER_FUND_INVESTOR_CAPITAL_ACTIVITIES_NOTIFICATION,
  [UiCapabilityKey.CanViewFundInvestments]:
    ApolloPermission.VIEW_FUND_INVESTMENTS,
  [UiCapabilityKey.CanViewFundInvestorDocuments]:
    ApolloPermission.VIEW_FUND_INVESTOR_DOCUMENTS,
  [UiCapabilityKey.CanUploadFundInvestmentDocuments]:
    ApolloPermission.UPLOAD_FUND_INVESTMENT_DOCUMENTS,
  [UiCapabilityKey.CanViewFundPage]: ApolloPermission.VIEW_FUND_PAGE,
  [UiCapabilityKey.CanViewFundSubscriptions]:
    ApolloPermission.VIEW_FUND_SUBSCRIPTIONS,
  [UiCapabilityKey.CanViewFundCapitalActivities]:
    ApolloPermission.VIEW_FUND_CAPITAL_ACTIVITIES,
  [UiCapabilityKey.CanViewFundInvestmentUserAccess]:
    ApolloPermission.VIEW_FUND_INVESTMENT_USER_ACCESS,
  [UiCapabilityKey.CanManageFundInvestmentUserAccess]:
    ApolloPermission.MANAGE_FUND_INVESTMENT_USER_ACCESS,
  [UiCapabilityKey.CanManageFundInvestmentMetrics]:
    ApolloPermission.MANAGE_FUND_INVESTMENT_METRICS,
  [UiCapabilityKey.CanManageOpportunityContacts]:
    ApolloPermission.MANAGE_OPPORTUNITY_CONTACTS,
  [UiCapabilityKey.CanManageFundCasConfigs]:
    ApolloPermission.MANAGE_FUND_CAS_CONFIGS,
  [UiCapabilityKey.CanAccessFund]: ApolloPermission.FUND_ACCESS,
  [UiCapabilityKey.CanAccessGeneralFundMaterials]:
    ApolloPermission.GENERAL_FUND_MATERIALS_ACCESS,
  [UiCapabilityKey.CanAccessDocumentAnalytics]:
    ApolloPermission.ACCESS_DOCUMENT_ANALYTICS,
  [UiCapabilityKey.CanViewOrgDetails]: ApolloPermission.VIEW_ORG_DETAILS,
  [UiCapabilityKey.CanViewUserDetails]: ApolloPermission.VIEW_USER_DETAILS,
  [UiCapabilityKey.CanEditUserDetails]: ApolloPermission.EDIT_USER_DETAILS,
  [UiCapabilityKey.CanManageUserDetails]: ApolloPermission.MANAGE_USER_DETAILS,
  [UiCapabilityKey.CanViewUserCarry]: ApolloPermission.VIEW_USER_CARRY,
  [UiCapabilityKey.CanViewWorkbenchCollaborators]:
    ApolloPermission.VIEW_WORKBENCH_COLLABORATORS,
  [UiCapabilityKey.CanManageWorkbenchCollaborators]:
    ApolloPermission.MANAGE_WORKBENCH_COLLABORATORS,
  [UiCapabilityKey.CanViewGeneralPlatformAnalytics]:
    ApolloPermission.VIEW_GENERAL_PLATFORM_ANALYTICS,
  [UiCapabilityKey.CanActAsVegaAdmin]: ApolloPermission.ACT_AS_VEGA_ADMIN,
  [UiCapabilityKey.CanEditEducation]: ApolloPermission.EDUCATION_EDIT,
  [UiCapabilityKey.CanEditEducationPages]:
    ApolloPermission.EDUCATION_PAGES_EDIT,
  [UiCapabilityKey.CanTriggerOcrFunctionality]:
    ApolloPermission.TRIGGER_OCR_FUNCTIONALITY,
  [UiCapabilityKey.CanActionSubscriptionTracker]:
    ApolloPermission.ACTION_SUBSCRIPTION_TRACKER,
  [UiCapabilityKey.CanViewEmployeeSubscriptionTracker]:
    ApolloPermission.VIEW_EMPLOYEE_SUBSCRIPTION_TRACKER,
  [UiCapabilityKey.CanViewThirdPartySubscriptionTracker]:
    ApolloPermission.VIEW_THIRD_PARTY_SUBSCRIPTION_TRACKER,
  [UiCapabilityKey.CanDownloadSubscriptionOpinionIssuanceReports]:
    ApolloPermission.DOWNLOAD_SUBSCRIPTION_OPINION_ISSUANCE_REPORTS,
  [UiCapabilityKey.CanSubmitCase]: ApolloPermission.SUBMIT_CASE,
  [UiCapabilityKey.CanAssignCase]: ApolloPermission.ASSIGN_CASE,
  [UiCapabilityKey.CanCloseCase]: ApolloPermission.CLOSE_CASE,
  [UiCapabilityKey.CanViewAllCases]: ApolloPermission.VIEW_ALL_CASES,
  [UiCapabilityKey.CanUpdateCase]: ApolloPermission.UPDATE_CASE,
  [UiCapabilityKey.CanDeleteCase]: ApolloPermission.DELETE_CASE,
  [UiCapabilityKey.CanCommentOnCase]: ApolloPermission.COMMENT_ON_CASE,
  [UiCapabilityKey.CanSendCaseEmail]: ApolloPermission.SEND_CASE_EMAIL,
  [UiCapabilityKey.CanManageCaseAttachments]:
    ApolloPermission.MANAGE_CASE_ATTACHMENTS,
  [UiCapabilityKey.CanConfigureCases]: ApolloPermission.CONFIGURE_CASES,
  [UiCapabilityKey.CanCreateCaseTemplate]:
    ApolloPermission.CREATE_CASE_TEMPLATE,
  [UiCapabilityKey.CanUpdateCaseTemplate]:
    ApolloPermission.UPDATE_CASE_TEMPLATE,
};
